import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Search, Plus, ShieldCheck, Landmark, Check, Loader2, ArrowRight, Eye, EyeOff, Star } from "lucide-react";
import { toast } from "sonner";
import { api, auth, errMsg } from "../lib/api";
import { Btn } from "../components/UI";

const SESSION_KEY = "tr_campaign_picked";
const label = (s) => (s || "").replace(/_/g, " ");
const safeNext = (n) => (n && n.startsWith("/") && !n.startsWith("//") ? n : "/dashboard");

const EntityRow = ({ e, active, onPick, onPin, busy }) => (
  <div className={`w-full rounded-xl border flex items-center gap-2 pr-2 transition-colors ${active ? "border-amber-400/60 bg-amber-400/10" : "border-white/10 bg-white/[0.02] hover:border-amber-400/40 hover:bg-white/[0.04]"}`} data-testid={`entity-row-${e.id}`}>
  <button onClick={() => onPick(e)} disabled={busy} data-testid={`pick-entity-${e.id}`}
    className="flex-1 min-w-0 text-left px-4 py-3 flex items-center gap-4">
    <span className="w-10 h-10 rounded-lg bg-white/5 grid place-items-center shrink-0"><Landmark className="w-5 h-5 text-amber-300" /></span>
    <span className="min-w-0 flex-1">
      <span className="flex items-center gap-2 flex-wrap">
        <span className="text-sm font-semibold text-white truncate">{e.public_campaign_name || e.legal_org_name}</span>
        {e.verification_summary?.score >= 85 && <ShieldCheck className="w-3.5 h-3.5 text-emerald-300" title="Verified political entity" />}
        {active && <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-400/15 text-amber-300 border border-amber-400/30" data-testid="last-used-badge">Last used</span>}
        {e.is_demo && <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 text-slate-400 border border-white/10">demo / test</span>}
      </span>
      <span className="block text-[11px] text-slate-400 truncate mt-0.5">
        {[label(e.entity_type), e.candidate_name, [e.office, e.state].filter(Boolean).join(" · ")].filter(Boolean).join("  ·  ")}
        {e.my_roles?.length ? `  ·  you: ${e.my_roles.map(label).join(", ")}` : ""}
      </span>
    </span>
    {active ? <Check className="w-4 h-4 text-amber-300 shrink-0" /> : <ArrowRight className="w-4 h-4 text-slate-500 shrink-0" />}
  </button>
  <button onClick={(ev) => { ev.stopPropagation(); onPin(e); }} disabled={busy} title={e.pinned ? "Unpin" : "Pin to top"} aria-pressed={!!e.pinned}
    data-testid={`pin-entity-${e.id}`} className={`p-2 rounded-lg shrink-0 transition-colors ${e.pinned ? "text-amber-300" : "text-slate-600 hover:text-amber-200"}`}>
    <Star className="w-4 h-4" fill={e.pinned ? "currentColor" : "none"} />
  </button>
  </div>
);

export default function SelectCampaign() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = safeNext(params.get("next"));
  const force = params.get("force") === "1"; // opened from the org switcher — always show, ignore skip preference
  const user = auth.getUser();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [showDemo, setShowDemo] = useState(false);
  const [remember, setRemember] = useState(false);
  const [busy, setBusy] = useState(false);

  const proceed = (eid) => {
    sessionStorage.setItem(SESSION_KEY, eid || "none");
    navigate(next, { replace: true });
  };

  useEffect(() => {
    api.get("/political-os/my-entities").then(({ data }) => {
      const ents = data.entities || [];
      const isStaff = !!data.is_staff;
      const activeValid = ents.some((e) => e.id === data.active_entity_id);
      if (ents.length === 0) return proceed(null);
      if (!force && data.picker_skip && activeValid) return proceed(data.active_entity_id);
      if (!force && !isStaff && ents.length === 1) {
        const only = ents[0];
        const go = only.id === data.active_entity_id ? Promise.resolve() : api.post(`/political-os/entities/${only.id}/activate`);
        return go.then(() => proceed(only.id)).catch(() => proceed(only.id));
      }
      setRemember(!!data.picker_skip);
      setShowDemo(ents.length > 0 && ents.every((e) => e.is_demo));
      setData(data);
    }).catch(() => proceed(null));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const list = useMemo(() => {
    const ents = data?.entities || [];
    const needle = q.trim().toLowerCase();
    return ents
      .filter((e) => showDemo || !e.is_demo)
      .filter((e) => !needle || `${e.public_campaign_name} ${e.legal_org_name} ${e.candidate_name} ${e.state} ${e.office}`.toLowerCase().includes(needle))
      .sort((a, b) => (a.pinned !== b.pinned ? (a.pinned ? -1 : 1) : a.id === data.active_entity_id ? -1 : b.id === data.active_entity_id ? 1 : (a.public_campaign_name || "").localeCompare(b.public_campaign_name || "")));
  }, [data, q, showDemo]);
  const pinnedCount = list.filter((e) => e.pinned).length;

  const togglePin = async (e) => {
    try {
      const { data: res } = await api.post(`/political-os/entities/${e.id}/pin`);
      setData((d) => ({ ...d, pinned_ids: res.pinned_ids, entities: d.entities.map((x) => ({ ...x, pinned: res.pinned_ids.includes(x.id) })) }));
      toast.success(res.pinned ? `Pinned ${e.public_campaign_name || e.legal_org_name}` : "Unpinned");
    } catch (err) { toast.error(errMsg(err, "Could not update pin")); }
  };

  const pick = async (e) => {
    setBusy(true);
    try {
      await api.post(`/political-os/entities/${e.id}/activate`, { remember });
      toast.success(`Working in ${e.public_campaign_name || e.legal_org_name}`);
      proceed(e.id);
    } catch { toast.error("Could not open that campaign"); setBusy(false); }
  };

  if (!data) {
    return <div className="min-h-screen grid place-items-center text-slate-400 text-sm" data-testid="select-campaign-loading"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading your campaigns…</div>;
  }
  const demoCount = data.entities.filter((e) => e.is_demo).length;

  return (
    <div className="min-h-screen px-4 py-10 md:py-16" data-testid="select-campaign-page">
      <div className="max-w-2xl mx-auto space-y-6">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-amber-300/80">Welcome back{user?.name ? `, ${user.name.split(" ")[0]}` : ""}</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold mt-1">Which campaign are you working on?</h1>
          <p className="text-sm text-slate-400 mt-2">Everything — dashboards, ads, fundraising, AI — is scoped to the campaign you pick. You can switch any time from the sidebar.</p>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by campaign, candidate, state…" autoFocus data-testid="campaign-search-input"
              className="w-full bg-white/5 border border-white/10 rounded-xl pl-9 pr-3 py-2.5 text-sm focus:border-amber-400/50 focus:outline-none" />
          </div>
          {demoCount > 0 && (
            <button onClick={() => setShowDemo((v) => !v)} data-testid="toggle-demo-entities"
              className="flex items-center gap-2 text-xs text-slate-400 hover:text-slate-200 px-3 py-2 rounded-lg border border-white/10">
              {showDemo ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
              {showDemo ? "Hide" : "Show"} demo & test campaigns ({demoCount})
            </button>
          )}
        </div>

        <div className="space-y-2 max-h-[52vh] overflow-y-auto pr-1" data-testid="campaign-list">
          {list.length === 0 && (
            <div className="text-sm text-slate-500 border border-dashed border-white/10 rounded-xl p-6 text-center" data-testid="campaign-list-empty">
              {q ? "No campaigns match your search." : "No live campaigns yet — show demo campaigns or add a new one."}
            </div>
          )}
          {list.map((e, i) => (
            <React.Fragment key={e.id}>
              {i === 0 && pinnedCount > 0 && <div className="text-[10px] uppercase tracking-[0.2em] text-amber-300/80 font-medium px-1 pt-1 inline-flex items-center gap-1" data-testid="pinned-group-label"><Star className="w-3 h-3" fill="currentColor" /> Pinned</div>}
              {i === pinnedCount && pinnedCount > 0 && i < list.length && <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-medium px-1 pt-2" data-testid="others-group-label">All campaigns</div>}
              <EntityRow e={e} active={e.id === data.active_entity_id} onPick={pick} onPin={togglePin} busy={busy} />
            </React.Fragment>
          ))}
        </div>

        <div className="flex items-center justify-between gap-3 flex-wrap pt-2 border-t border-white/10">
          <label className="flex items-center gap-2 text-xs text-slate-400">
            <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} data-testid="remember-choice-checkbox" />
            Remember my choice and skip this screen next time
          </label>
          <div className="flex items-center gap-2">
            <Btn variant="secondary" onClick={() => navigate("/onboarding/political")} data-testid="add-campaign-btn"><Plus className="w-4 h-4" /> Add a campaign</Btn>
            {data.is_staff && (
              <button onClick={() => proceed(data.active_entity_id)} className="text-xs text-slate-500 hover:text-slate-300 px-2" data-testid="continue-without-picking-btn">
                Continue with current
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
